import React, { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth, defaultPathFor } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { ItcotLogo } from "@/components/ItcotLogo";
import { PasswordInput } from "@/components/PasswordInput";
import { api } from "@/lib/api";
import { ShieldCheck, HardHat, ClipboardCheck, UserCheck, Building } from "lucide-react";

const ROLES = [
  { id: "admin", label: "Admin", icon: ShieldCheck, titleFn: () => "Admin Login" },
  { id: "pmc_supervisor", label: "PMC Supervisor", icon: ClipboardCheck, titleFn: () => "PMC Supervisor Login" },
  { id: "contractor", label: "Contractor", icon: HardHat, titleFn: (b) => `${b.contractor_brand_name || "Contractor"} Login` },
];

const CONTRACTOR_ACCOUNTS = [
  {
    role: "Contractor Supervisor",
    username: "contractor_supervisor",
    icon: HardHat,
    desc: "Field operations & attendance tracking",
  },
  {
    role: "Supervisor In Charge",
    username: "supervisor_incharge",
    icon: UserCheck,
    desc: "Backup supervisor operations & attendance",
  },
  {
    role: "Office Admin",
    username: "office_admin",
    icon: Building,
    desc: "Operations Dashboard & office management",
  },
];

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [activeRole, setActiveRole] = useState("admin");
  const [selectedContractorRole, setSelectedContractorRole] = useState(null);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [branding, setBranding] = useState({
    app_name: "SIPCOT Industrial Park Upkeeping Monitoring Application",
    owner: "ITCOT Limited",
    owner_address: "50A, Greams Road, Chennai 600 006",
    owner_website: "www.itcot.com",
    contractor_brand_name: "Our Land Engineering Works",
  });

  useEffect(() => {
    api.get("/public/branding").then((r) => setBranding(r.data)).catch(() => { });
  }, []);

  if (user) return <Navigate to={defaultPathFor(user.role, user)} replace />;

  const onSubmit = async (e) => {
    e?.preventDefault?.();
    if (!username || !password) return;
    setSubmitting(true);
    try {
      const u = await login(username.trim().toLowerCase(), password);
      const isContractorRole = u.role === "contractor_supervisor" || u.role === "contractor_admin" || u.role === "supervisor_incharge";
      const roleMatch = (activeRole === "contractor" && isContractorRole) || u.role === activeRole;
      if (!roleMatch) {
        toast.warning(`Signed in as ${u.role.replace("_", " ")}`);
      } else {
        toast.success(`Welcome, ${u.full_name || u.username}`);
      }
      navigate(defaultPathFor(u.role, u));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  const role = ROLES.find((r) => r.id === activeRole) || ROLES[0];
  const year = new Date().getFullYear();

  const handleQuickSelect = (acc) => {
    setSelectedContractorRole(acc.role);
    setUsername("");
    setPassword("");
    toast.info(`Selected ${acc.role} role — please enter credentials`);
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <Card className="w-full max-w-md p-6 sm:p-7 shadow-[0_12px_30px_rgba(15,23,42,0.08)]">
          <div className="flex flex-col items-center text-center mb-5">
            <ItcotLogo className="h-14 sm:h-16" />
            <h1 className="font-display text-base sm:text-lg font-semibold mt-3 leading-snug">
              SIPCOT Industrial Park Upkeeping Monitoring Application
            </h1>
          </div>

          <div className="grid grid-cols-3 gap-1 rounded-xl bg-secondary p-1 mb-5" role="tablist" aria-label="Login as">
            {ROLES.map((r) => {
              const Icon = r.icon;
              const active = r.id === activeRole;
              return (
                <button
                  key={r.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => {
                    setActiveRole(r.id);
                    setUsername("");
                    setPassword("");
                  }}
                  className={`flex items-center justify-center gap-1.5 rounded-lg px-2.5 py-2 text-xs font-medium transition-colors ${active ? "bg-background text-foreground shadow-sm font-semibold" : "text-muted-foreground hover:text-foreground"
                    }`}
                  data-testid={`login-tab-${r.id}`}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span className="truncate">{r.label}</span>
                </button>
              );
            })}
          </div>

          <div className="mb-4">
            <h2 className="font-display text-lg font-semibold leading-tight" data-testid="login-role-title">
              {role.titleFn(branding)}
            </h2>
          </div>

          {activeRole === "contractor" && (
            <div className="mb-5 border-t border-b border-amber-200/60 bg-amber-50/40 p-3 rounded-lg">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-amber-900 mb-2">
                Contractor User Accounts
              </div>
              <div className="flex flex-col gap-1.5">
                {CONTRACTOR_ACCOUNTS.map((acc) => {
                  const Icon = acc.icon;
                  const isSelected = selectedContractorRole === acc.role;
                  return (
                    <button
                      key={acc.role}
                      type="button"
                      onClick={() => handleQuickSelect(acc)}
                      className={`flex items-center gap-2.5 p-2 rounded-md border text-left transition-all ${isSelected
                        ? "border-amber-500 bg-amber-100/70 ring-1 ring-amber-500 font-semibold"
                        : "border-slate-200/80 bg-white hover:border-amber-300 hover:bg-amber-50/50"
                        }`}
                      data-testid={`select-account-${acc.username}`}
                    >
                      <div className={`p-1.5 rounded ${isSelected ? "bg-amber-600 text-white" : "bg-slate-100 text-slate-600"}`}>
                        <Icon className="h-3.5 w-3.5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-medium text-slate-900 leading-tight">
                          {acc.role}
                        </div>
                        <div className="text-[10px] text-slate-500">
                          {acc.desc}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <form className="flex flex-col gap-3" onSubmit={onSubmit}>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="username">Username</Label>
              <Input id="username" value={username} onChange={(e) => setUsername(e.target.value)} autoFocus autoComplete="username" data-testid="login-username-input" />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="password">Password</Label>
              <PasswordInput id="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" data-testid="login-password-input" testIdToggle="login-password-toggle" />
            </div>
            <Button type="submit" disabled={submitting} className="mt-2" data-testid="login-submit-button">
              {submitting ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        </Card>
      </main>

      <footer className="px-4 pb-8 pt-4 text-center text-[12px] text-muted-foreground leading-relaxed" data-testid="login-footer">
        <div className="font-medium text-foreground">Owned by {branding.owner}</div>
        <div>{branding.owner_address}</div>
        <div className="mt-0.5">
          © {year} {branding.owner} {" | "}
          <a className="underline-offset-2 hover:underline" href={`https://${branding.owner_website}`} target="_blank" rel="noreferrer">
            {branding.owner_website}
          </a>
        </div>
      </footer>
    </div>
  );
}


